import { ChangeDetectionStrategy, Component, effect, input, signal, WritableSignal } from '@angular/core';
import { ButtonComponent } from '../../buttons/button/button.component';
import { ButtonType } from '../../enum/button-type.enum';
import { InputSize } from '../../enum/input-size.enum';
import { TextInputComponent } from '../../form/inputs/text-input/text-input.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { Observable, take } from 'rxjs';
import { indicateSignal } from '../../../extensions/observable.extensions';
import { FormErrorComponent } from '../../form/form-error/form-error.component';
import { GeneralStatusPillComponent } from '../../pills/general-status-pill/general-status-pill.component';
import { StatusPillType } from '../../pills/general-status-pill/status-pill-type.enum';
import { CheckboxComponent } from '../../interactive-labels/checkbox/checkbox.component';

@Component({
  selector: 'lib-editable-row-card',
  imports: [
    ButtonComponent,
    TextInputComponent,
    ReactiveFormsModule,
    FormErrorComponent,
    GeneralStatusPillComponent,
    CheckboxComponent,
  ],
  templateUrl: './editable-row-card.component.html',
  styleUrl: './editable-row-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditableRowCardComponent {
  cardItem = input.required<EditableRowCardItem>();
  protected readonly ButtonType = ButtonType;
  protected readonly InputSize = InputSize;
  protected readonly StatusPillType = StatusPillType;
  protected isSaving = signal(false);
  protected inputId: string = uuidv4();
  protected inputFormControl = new FormControl<string>('', [Validators.required]);
  protected formGroup = new FormGroup({ inputFormControl: this.inputFormControl });

  private cardItemEffect = effect(() => {
    this.cardItem().isEditing.set(false);
    this.inputFormControl.setValue(this.cardItem().name());
  });

  private showCheckboxEffect = effect(() => {
    const showCheckbox = this.cardItem().showCheckbox();
    if (showCheckbox) {
      this.cancel();
    } else {
      this.cardItem().isChecked.set(false);
    }
  });

  protected editClicked() {
    this.cardItem().isEditing.set(true);
  }
  protected cancel() {
    this.inputFormControl.setValue(this.cardItem().name());
    this.cardItem().isEditing.set(false);
  }

  protected archiveClicked() {
    this.cancel();
    this.cardItem().archiveClicked?.();
  }

  protected saveClicked() {
    if (this.inputFormControl.invalid) {
      return;
    }
    const updatedValue = this.inputFormControl.value ?? '';
    this.formGroup.disable();
    this.cardItem()
      .saveClicked?.(updatedValue)
      .pipe(take(1), indicateSignal(this.isSaving))
      .subscribe({
        next: () => {
          this.formGroup.enable();
          this.cardItem().isEditing.set(false);
          this.cardItem().name.set(updatedValue);
        },
      });
  }

  protected checkboxClicked() {
    this.cardItem().isChecked.update(checked => !checked);
  }
}

export class EditableRowCardItem {
  name: WritableSignal<string>;
  id?: number | null | undefined;
  isArchived: WritableSignal<boolean>;
  showCheckbox = signal(false);
  isChecked = signal(false);
  isEditing = signal(false);
  readOnly = false;
  archiveClicked?: () => void;
  saveClicked?: (updatedName: string) => Observable<unknown>;

  constructor(
    name: WritableSignal<string>,
    id: number | null | undefined,
    isArchived: WritableSignal<boolean>,
    readOnly?: boolean,
    archiveClicked?: () => void,
    saveClicked?: (updatedName: string) => Observable<unknown>,
  ) {
    this.name = name;
    this.id = id;
    this.isArchived = isArchived;
    this.readOnly = readOnly ?? false;
    this.archiveClicked = archiveClicked;
    this.saveClicked = saveClicked;
  }
}
